/**
 * Unified build retry — mirrors FlowOps `resumeAutonomousBuild` / AgentChatView Retry.
 * One autonomous resume at a time per session (avoids abort storms).
 *
 * HITL / autonomous resume payloads for /api/workflow/message:
 * - Real answers are persisted as user turns (persistUserText / persistConfirmedAnswers).
 * - Steering instructions are seed — never restored as orange bubbles.
 */

import type { ConfirmedAnswer } from "@/shared/builder-session";
import { isSessionGoneError } from "../restore-session";

export const HITL_CONTINUE_INSTRUCTION =
  "Use the user's answer. Call get_session_status, follow recommendedNext, " +
  "and continue. If still planning with no plan, call submit_plan now — do not re-run discovery you already completed. " +
  "If building/testing, continue the build. Do not ask the same question again.";

export const HITL_REWIND_INSTRUCTION =
  "The user edited earlier answers. Anything after that point (intent, plan, tools) is void. " +
  "Use the updated answers. Call get_session_status, follow recommendedNext, and continue. " +
  "Do not ask the same questions again.";

export type RewindFromAnswers = {
  questions: string[];
  previousAnswers?: string[];
  clearIntent?: boolean;
  clearPlan?: boolean;
};

/** Original HITL MCQ — stored with answers so Edit reopens the same options. */
export type PersistQuestionCard = {
  questionId: string;
  questions: Array<{
    id: string;
    question: string;
    options: string[];
    multiSelect: boolean;
    context?: string;
  }>;
  drafts?: Record<string, { selected: string[]; custom: string }>;
};

/** Question-card answer: persist structured card + readable text; steer via seed. */
export function hitlAnswerResumeBody(
  sessionId: string,
  answerText: string,
  confirmedAnswers?: ConfirmedAnswer[],
  rewind?: RewindFromAnswers,
  questionCard?: PersistQuestionCard,
) {
  return {
    sessionId,
    autonomous: true,
    turnKind: "seed" as const,
    message: rewind ? HITL_REWIND_INSTRUCTION : HITL_CONTINUE_INSTRUCTION,
    persistUserText: answerText,
    ...(confirmedAnswers?.length ? { persistConfirmedAnswers: confirmedAnswers } : {}),
    ...(questionCard?.questions.length ? { persistQuestionCard: questionCard } : {}),
    ...(rewind ? { rewindFromAnswers: rewind } : {}),
  };
}

/** Composio / runtime / retry: seed-only (not a user chat bubble). */
export function autonomousResumeBody(sessionId: string, message: string, persistUserText?: string) {
  return {
    sessionId,
    autonomous: true,
    turnKind: "seed" as const,
    message,
    ...(persistUserText?.trim() ? { persistUserText: persistUserText.trim() } : {}),
  };
}

/** Public bubble for runtime-input submit — keep in sync with live appendMessage. */
export function formatRuntimeInputsUserText(inputs: Record<string, unknown>): string {
  const entries = Object.entries(inputs);
  if (entries.length === 0) return "Provided runtime inputs.";
  if (entries.length === 1) {
    const [name, value] = entries[0]!;
    return `Provided ${name}: ${String(value)}`;
  }
  return `Provided runtime inputs: ${JSON.stringify(inputs)}`;
}

/** When the parked durable run is gone — steer a fresh autonomous turn (FlowOps orphan). */
export function seedMessageAfterRuntimeInput(inputs: Record<string, unknown>): string {
  return (
    `The user submitted runtime inputs after a page refresh (no live waiter): ${JSON.stringify(inputs)}. ` +
    `Merge them into collected inputs / the next probe. Call get_session_status, follow recommendedNext, ` +
    `and continue autonomously until finalize_workflow succeeds. Do not re-request the same fields.`
  );
}

/**
 * Structured Q+A for Activity — same Answers confirmed card as MCQ HITL.
 * Prefer field description (what the user was asked); fall back to the field name.
 */
export function confirmedAnswersFromRuntimeInputs(
  fields: Array<{ name: string; description?: string }>,
  inputs: Record<string, unknown>,
): ConfirmedAnswer[] {
  const answers: ConfirmedAnswer[] = [];
  for (const field of fields) {
    if (!(field.name in inputs)) continue;
    const value = inputs[field.name];
    if (value === undefined || value === null || value === "") continue;
    const question = field.description?.trim() || `Provide a value for ${field.name}`;
    answers.push({ question, answer: String(value) });
  }
  if (answers.length > 0) return answers;
  // Fallback when fields list is empty but inputs exist.
  return Object.entries(inputs)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([name, value]) => ({
      question: `Provide a value for ${name}`,
      answer: String(value),
    }));
}

/** Upgrade plain "Provided name: value" bubbles (pre-structured runtime submit). */
export function legacyProvidedRuntimeAnswers(text: string): ConfirmedAnswer[] | null {
  const trimmed = text.trim();
  const single = /^Provided ([^:]+): ([\s\S]+)$/.exec(trimmed);
  if (single) {
    const name = single[1]!.trim();
    const answer = single[2]!.trim();
    if (!name || !answer) return null;
    return [{ question: `Provide a value for ${name}`, answer }];
  }
  return null;
}

export const DEFAULT_RESUME_MESSAGE =
  "Continue from this session. Call get_session_status and follow recommendedNext. " +
  "If still planning with no plan, call submit_plan now — do not finalize_workflow yet. " +
  "If building/testing, continue the build.";

export const PLANNING_RESUME_MESSAGE =
  "Continue from this session. Call get_session_status, then submit_plan for approval. " +
  "Do not ask whether to continue — produce and submit the plan now.";

export function seedMessageAfterComposioStale(input: {
  approved: boolean;
  toolSlug: string;
  arguments: Record<string, unknown>;
}): string {
  if (!input.approved) {
    return (
      `The previous approval UI expired (not found/timed out). The user clicked Reject for Composio tool "${input.toolSlug}". ` +
      `Do not execute that tool. Call get_session_status and follow recommendedNext. ` +
      `If still planning with no plan, call submit_plan now — do not finalize_workflow yet.`
    );
  }
  return (
    `The previous approval UI expired (not found/timed out). The user already clicked Accept for Composio tool "${input.toolSlug}" ` +
    `with arguments ${JSON.stringify(input.arguments)}. ` +
    `Call execute_composio_tool once with the same slug and arguments if you still need the result; ` +
    `skip if it already returned ok. Prefer any orphan grant. ` +
    `Then if still planning with no plan, call submit_plan immediately and STOP.`
  );
}

/** Shown when a real server/build stall is painted (not a client inactivity timer). */
export const STREAM_STALLED_MESSAGE =
  "Connection stalled while the agent was working. Retry to continue.";

const resumeInFlight = new Map<string, Promise<void>>();

export function resumeMessageForPhase(phase: string, hasPlan: boolean): string {
  if (phase === "planning" && !hasPlan) return PLANNING_RESUME_MESSAGE;
  return DEFAULT_RESUME_MESSAGE;
}

type StallTarget = {
  setBusy: (value: boolean) => void;
  setIsStoppingAgent: (value: boolean) => void;
  setBuildStalled: (value: boolean) => void;
  setError: (value: string | null) => void;
  appendMessage: (type: "error" | "status", text: string) => void;
};

/** Clear hung busy and show Retry — only for real stalls/errors (FlowOps parity). */
export function markBuildPausedForRetry(
  builder: StallTarget,
  message: string,
  options?: { asErrorMessage?: boolean },
): void {
  builder.setBusy(false);
  builder.setIsStoppingAgent(false);
  builder.setBuildStalled(true);
  builder.setError(message);
  if (options?.asErrorMessage) {
    builder.appendMessage("error", message);
  } else {
    builder.appendMessage("status", message);
  }
}

/** Whether the FlowOps-style Retry / Continue control should show. */
export function canShowBuildRetry(options: {
  busy: boolean;
  error: string | null;
  buildStalled: boolean;
  sessionId: string | null;
  sourcePrompt: string | null;
  awaitingQuestion: boolean;
  awaitingBlockingHitl: boolean;
}): boolean {
  const {
    busy,
    error,
    buildStalled,
    sessionId,
    sourcePrompt,
    awaitingQuestion,
    awaitingBlockingHitl,
  } = options;
  if (busy) return false;
  if (!error?.trim() && !buildStalled) return false;
  // Dead server session — Retry only repeats the 404.
  if (isSessionGoneError(error)) return false;
  if (!sessionId && !sourcePrompt?.trim()) return false;
  // Pending HITL has its own re-submit; resuming here would skip delivering input.
  if (awaitingQuestion || awaitingBlockingHitl) return false;
  return true;
}

/** After restore: auto-resume when mid-build with nothing pending for the user.
 * FlowOps hitl-orphan: building | paused | planning only — never testing
 * (draft-saved rest; Validate/Finalize are explicit user actions). */
export function shouldAutoResumeAfterRestore(phase: string, hasPendingHitl: boolean): boolean {
  if (hasPendingHitl) return false;
  return phase === "building" || phase === "paused" || phase === "planning" || phase === "working";
}

export async function resumeAutonomousBuild(options: {
  sessionId: string;
  message?: string;
  stream: (url: string, body: Record<string, unknown>) => Promise<void>;
}): Promise<void> {
  const { sessionId, stream } = options;
  const message = options.message ?? DEFAULT_RESUME_MESSAGE;

  const existing = resumeInFlight.get(sessionId);
  if (existing) return existing;

  const pending = (async () => {
    await stream("/api/workflow/message", autonomousResumeBody(sessionId, message));
  })().finally(() => {
    if (resumeInFlight.get(sessionId) === pending) {
      resumeInFlight.delete(sessionId);
    }
  });

  resumeInFlight.set(sessionId, pending);
  return pending;
}

/** Test-only: clear resume single-flight latch. */
export function resetResumeAutonomousBuildForTests(): void {
  resumeInFlight.clear();
}
